import { useT } from '@insurensa/i18n';
import { useMemo, useState, type ReactNode } from 'react';
import { exportCopy, exportCsv, exportExcel, exportPrint, type ExportPayload } from './export.js';

/**
 * The DataTables replacement.
 *
 * The legacy portals initialised DataTables 1.10.21 with `dom: "Blfrtip"` —
 * Buttons, length selector, filter, table, info, pagination — on every lead and
 * offer list. This reproduces that layout and behaviour, emitting the same
 * `.dataTables_*` class names that `insurensa-tables.css` styles.
 *
 * It supports both modes:
 *   * client-side  — sorting, search and paging over `rows` in memory
 *   * server-side  — pass `pagination` and `onSearch`, and the component defers
 *                    to the API instead, which is what the lead and offer lists
 *                    use because those datasets are unbounded
 *
 * Export always covers the full `rows` array the component holds. With
 * server-side paging that is the current page; `onExportAll` lets a caller
 * fetch everything first, matching what DataTables did when it held the whole
 * table client-side.
 */

export interface DataTableColumn<T> {
  key: string;
  header: string;
  /** Cell content. Falls back to the exported string value. */
  render?: (row: T) => ReactNode;
  /** Plain text for sorting and export. */
  value?: (row: T) => string;
  sortable?: boolean;
  /** Right-aligns and uses tabular numerals. */
  numeric?: boolean;
  className?: string;
  /** Excluded from Copy/CSV/Excel/PDF/Print, e.g. an actions column. */
  exportable?: boolean;
  width?: string;
}

export interface DataTablePagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPage: (page: number) => void;
  onPageSize?: (pageSize: number) => void;
}

export interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Base name for exported files, e.g. "kfz-anfragen". */
  exportFileName?: string;
  /** Heading used in the printed and Excel output. */
  exportTitle?: string | undefined;
  /** Fetches the complete set for export when paging server-side. */
  onExportAll?: () => Promise<T[]>;
  /** Server-side paging. Omit for client-side. */
  pagination?: DataTablePagination;
  /** Server-side search. Omit for client-side filtering. */
  search?: { value: string; onChange: (value: string) => void };
  emptyMessage?: string | undefined;
  /** Extra controls rendered beside the export buttons. */
  toolbar?: ReactNode;
  onRowClick?: (row: T) => void;
}

const PAGE_SIZES = [10, 25, 50, 100];

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  exportFileName = 'export',
  exportTitle,
  onExportAll,
  pagination,
  search,
  emptyMessage,
  toolbar,
  onRowClick,
}: DataTableProps<T>) {
  const t = useT();
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [localSearch, setLocalSearch] = useState('');
  const [localPage, setLocalPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(10);
  const [exporting, setExporting] = useState(false);

  const serverSide = Boolean(pagination);
  const searchValue = search ? search.value : localSearch;

  const textOf = (row: T, column: DataTableColumn<T>): string => {
    if (column.value) return column.value(row);
    const raw = (row as Record<string, unknown>)[column.key];
    return raw === null || raw === undefined ? '' : String(raw);
  };

  /** Client-side filter. Skipped entirely when the server is doing it. */
  const filtered = useMemo(() => {
    if (serverSide || searchValue.trim() === '') return rows;
    const needle = searchValue.toLowerCase();
    return rows.filter((row) =>
      columns.some((column) => textOf(row, column).toLowerCase().includes(needle)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns, searchValue, serverSide]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((candidate) => candidate.key === sort.key);
    if (!column) return filtered;

    const collator = new Intl.Collator('de-DE', { numeric: true, sensitivity: 'base' });
    return [...filtered].sort((a, b) => {
      const result = collator.compare(textOf(a, column), textOf(b, column));
      return sort.direction === 'asc' ? result : -result;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort, columns]);

  const paged = useMemo(() => {
    if (serverSide) return sorted;
    const start = (localPage - 1) * localPageSize;
    return sorted.slice(start, start + localPageSize);
  }, [sorted, serverSide, localPage, localPageSize]);

  const page = pagination?.page ?? localPage;
  const pageSize = pagination?.pageSize ?? localPageSize;
  const total = pagination?.total ?? sorted.length;
  const totalPages = pagination?.totalPages ?? Math.max(1, Math.ceil(sorted.length / localPageSize));

  const goToPage = (next: number): void => {
    if (next < 1 || next > totalPages) return;
    if (pagination) pagination.onPage(next);
    else setLocalPage(next);
  };

  const changePageSize = (next: number): void => {
    if (pagination?.onPageSize) pagination.onPageSize(next);
    else {
      setLocalPageSize(next);
      setLocalPage(1);
    }
  };

  const toggleSort = (column: DataTableColumn<T>): void => {
    if (column.sortable === false) return;
    setSort((current) => {
      if (!current || current.key !== column.key) return { key: column.key, direction: 'asc' };
      if (current.direction === 'asc') return { key: column.key, direction: 'desc' };
      return null;
    });
  };

  const buildPayload = (source: T[]): ExportPayload => {
    const exportColumns = columns.filter((column) => column.exportable !== false);
    return {
      fileName: exportFileName,
      title: exportTitle ?? exportFileName,
      headers: exportColumns.map((column) => column.header),
      rows: source.map((row) => exportColumns.map((column) => textOf(row, column))),
    };
  };

  const runExport = async (action: (payload: ExportPayload) => void | Promise<void>): Promise<void> => {
    setExporting(true);
    try {
      const source = onExportAll ? await onExportAll() : sorted;
      await action(buildPayload(source));
    } finally {
      setExporting(false);
    }
  };

  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, total);

  return (
    <div className="dataTables_wrapper dt-bootstrap4">
      {/* dom: "Blfrtip" — Buttons first, then length and filter. */}
      <div className="row">
        <div className="col-sm-12 col-md-8">
          <div className="dt-buttons btn-group flex-wrap">
            <button
              type="button"
              className="btn btn-secondary buttons-copy"
              disabled={exporting || total === 0}
              onClick={() => void runExport(exportCopy)}
            >
              <span>{t('table.copy')}</span>
            </button>
            <button
              type="button"
              className="btn btn-secondary buttons-csv"
              disabled={exporting || total === 0}
              onClick={() => void runExport(exportCsv)}
            >
              <span>{t('table.csv')}</span>
            </button>
            <button
              type="button"
              className="btn btn-secondary buttons-excel"
              disabled={exporting || total === 0}
              onClick={() => void runExport(exportExcel)}
            >
              <span>{t('table.excel')}</span>
            </button>
            <button
              type="button"
              className="btn btn-secondary buttons-pdf"
              disabled={exporting || total === 0}
              onClick={() => void runExport(exportPrint)}
            >
              <span>{t('table.pdf')}</span>
            </button>
            <button
              type="button"
              className="btn btn-secondary buttons-print"
              disabled={exporting || total === 0}
              onClick={() => void runExport(exportPrint)}
            >
              <span>{t('table.print')}</span>
            </button>
          </div>
          {toolbar}
        </div>

        <div className="col-sm-12 col-md-4">
          <div className="dataTables_filter">
            <label>
              {t('table.search')}
              <input
                type="search"
                className="form-control form-control-sm"
                value={searchValue}
                onChange={(event) => {
                  if (search) search.onChange(event.target.value);
                  else {
                    setLocalSearch(event.target.value);
                    setLocalPage(1);
                  }
                }}
              />
            </label>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="table-responsive">
            <table className="table table-hover table-striped table-bordered dataTable">
              <thead>
                <tr>
                  {columns.map((column) => {
                    const active = sort?.key === column.key;
                    const sortable = column.sortable !== false;
                    return (
                      <th
                        key={column.key}
                        style={column.width ? { width: column.width } : undefined}
                        className={[
                          sortable ? 'sorting' : '',
                          active ? (sort?.direction === 'asc' ? 'sorting_asc' : 'sorting_desc') : '',
                          column.numeric ? 'text-right' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => toggleSort(column)}
                        role={sortable ? 'button' : undefined}
                        aria-sort={
                          active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                        }
                      >
                        {column.header}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 5 }, (_, rowIndex) => (
                    <tr key={`skeleton-${rowIndex}`}>
                      {columns.map((column) => (
                        <td key={column.key}>
                          <span
                            className="dt-skeleton"
                            style={{ width: `${45 + ((rowIndex * 11 + column.key.length * 7) % 45)}%` }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="dataTables_empty text-center">
                      {emptyMessage ?? t('table.noData')}
                    </td>
                  </tr>
                ) : (
                  paged.map((row) => (
                    <tr
                      key={rowKey(row)}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      style={onRowClick ? { cursor: 'pointer' } : undefined}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          // Carries the column name so the table can restack as
                          // labelled rows on a phone, where a 7-column grid is
                          // unreadable.
                          data-label={typeof column.header === 'string' ? column.header : undefined}
                          className={[column.className ?? '', column.numeric ? 'text-right' : '']
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {column.render ? column.render(row) : textOf(row, column)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12 col-md-5">
          <div className="dataTables_length">
            <label>
              Zeige
              <select
                className="custom-select custom-select-sm form-control form-control-sm"
                value={pageSize}
                onChange={(event) => changePageSize(Number(event.target.value))}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              {t('table.entries')}
            </label>
          </div>
          <div className="dataTables_info" role="status" aria-live="polite">
            {total === 0
              ? t('table.noEntries')
              : t('table.showing', { first: firstRow, last: lastRow, total })}
          </div>
        </div>

        <div className="col-sm-12 col-md-7">
          <div className="dataTables_paginate paging_simple_numbers">
            <ul className="pagination">
              <li className={`paginate_button page-item previous ${page <= 1 ? 'disabled' : ''}`}>
                <button type="button" className="page-link" onClick={() => goToPage(page - 1)}>
                  {t('table.previous')}
                </button>
              </li>

              {pageNumbers(page, totalPages).map((entry, index) =>
                entry === '…' ? (
                  <li key={`gap-${index}`} className="paginate_button page-item disabled">
                    <span className="page-link">…</span>
                  </li>
                ) : (
                  <li
                    key={entry}
                    className={`paginate_button page-item ${entry === page ? 'active' : ''}`}
                  >
                    <button type="button" className="page-link" onClick={() => goToPage(entry)}>
                      {entry}
                    </button>
                  </li>
                ),
              )}

              <li className={`paginate_button page-item next ${page >= totalPages ? 'disabled' : ''}`}>
                <button type="button" className="page-link" onClick={() => goToPage(page + 1)}>
                  {t('table.next')}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Windowed page numbers with ellipses, as DataTables renders them. */
function pageNumbers(current: number, totalPages: number): Array<number | '…'> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);

  if (start > 2) pages.push('…');
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < totalPages - 1) pages.push('…');
  pages.push(totalPages);

  return pages;
}
